import type { ProjectFile } from '@archer/shared';
import type { ProjectSummary, StorageAdapter } from './types';

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>;
  }
}

interface FileSystemFileHandleWithContent {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
  createWritable(options?: { keepExistingData?: boolean }): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream {
  write(data: string | Blob | ArrayBuffer): Promise<void>;
  close(): Promise<void>;
}

interface FileSystemDirectoryHandle {
  kind: 'directory';
  name: string;
  values(): AsyncIterable<FileSystemFileHandle | FileSystemDirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandleWithContent>;
  removeEntry(name: string): Promise<void>;
}

interface FileSystemFileHandle {
  kind: 'file';
  name: string;
}

const INDEX_FILE = '.archer-index.json';
const SAFE_NAME = (s: string): string => s.replace(/[\\/:*?"<>|]/g, '_').slice(0, 80) || 'untitled';
const FILE_NAME = (id: string, name: string): string => `${SAFE_NAME(name)}_${id}.archer.json`;

interface IndexEntry {
  id: string;
  name: string;
  updatedAt: string;
  thumbnail?: string;
  fileName: string;
}

export class LocalFsStorageAdapter implements StorageAdapter {
  readonly kind = 'local' as const;
  readonly label = 'Local folder';

  constructor(private directoryHandle: FileSystemDirectoryHandle) {}

  static isSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
  }

  static async pick(): Promise<LocalFsStorageAdapter> {
    if (!window.showDirectoryPicker) {
      throw new Error('File System Access API not supported in this browser. Use Chromium/Edge.');
    }
    const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
    return new LocalFsStorageAdapter(handle as unknown as FileSystemDirectoryHandle);
  }

  private async readIndex(): Promise<IndexEntry[]> {
    try {
      const fileHandle = await this.directoryHandle.getFileHandle(INDEX_FILE);
      const file = await fileHandle.getFile();
      const text = await file.text();
      return JSON.parse(text) as IndexEntry[];
    } catch {
      return [];
    }
  }

  private async writeIndex(entries: IndexEntry[]): Promise<void> {
    const handle = await this.directoryHandle.getFileHandle(INDEX_FILE, { create: true });
    const writable = await handle.createWritable();
    await writable.write(JSON.stringify(entries, null, 2));
    await writable.close();
  }

  async list(): Promise<ProjectSummary[]> {
    const entries = await this.readIndex();
    return entries.map((e) => ({
      id: e.id,
      name: e.name,
      ownerId: 'local',
      thumbnail: e.thumbnail,
      schemaVersion: 1,
      createdAt: e.updatedAt,
      updatedAt: e.updatedAt,
      storageKind: 'local',
      storageRef: e.fileName,
    }));
  }

  async load(storageRef: string): Promise<ProjectFile> {
    const handle = await this.directoryHandle.getFileHandle(storageRef);
    const file = await handle.getFile();
    return JSON.parse(await file.text()) as ProjectFile;
  }

  async save(storageRef: string, file: ProjectFile): Promise<void> {
    const handle = await this.directoryHandle.getFileHandle(storageRef, { create: true });
    const writable = await handle.createWritable();
    await writable.write(JSON.stringify(file, null, 2));
    await writable.close();
    const entries = await this.readIndex();
    const filtered = entries.filter((e) => e.fileName !== storageRef);
    filtered.unshift({
      id: file.id,
      name: file.name,
      updatedAt: file.updatedAt,
      thumbnail: file.thumbnail,
      fileName: storageRef,
    });
    await this.writeIndex(filtered);
  }

  async remove(storageRef: string): Promise<void> {
    await this.directoryHandle.removeEntry(storageRef);
    const entries = await this.readIndex();
    await this.writeIndex(entries.filter((e) => e.fileName !== storageRef));
  }

  static fileNameFor(id: string, name: string): string {
    return FILE_NAME(id, name);
  }
}
